import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Typewriter from 'typewriter-effect';
import { motion, useInView, useReducedMotion } from 'framer-motion';
import {
  BookOpen,
  Users,
  GraduationCap,
  FileText,
  Brain,
  Globe,
  Award,
  Heart,
  Mail,
  Activity,
  Zap,
  Clock,
  Stethoscope,
  Sparkles,
  Leaf,
} from 'lucide-react';
import { founderPhoto } from '../assets/images';
import '../styles/home-enhance.css';

const Reveal = ({ children, delay = 0, className = '', y = 24 }) => {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.55, delay, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const CountUp = ({ value, suffix = '', className = '' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const reduce = useReducedMotion();
  const [display, setDisplay] = useState(reduce ? value : 0);

  useEffect(() => {
    if (!inView) return;
    if (reduce) { setDisplay(value); return; }
    let raf;
    const start = performance.now();
    const dur = 1500;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setDisplay(Math.round((1 - Math.pow(1 - p, 3)) * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, reduce, value]);

  return <span ref={ref} className={className}>{display}{suffix}</span>;
};

const RoadmapProgress = ({ status }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-30px' });
  const reduce = useReducedMotion();
  const target = { 'in-development': 55, planning: 25, planned: 12 }[status] ?? 20;
  return (
    <div ref={ref} className="progress-bar">
      <motion.div
        className={`progress-fill ${status}`}
        initial={reduce ? { width: `${target}%` } : { width: 0 }}
        animate={inView ? { width: `${target}%` } : {}}
        transition={{ duration: reduce ? 0 : 1.1, ease: 'easeOut' }}
      />
    </div>
  );
};

const About = () => {
  const reduce = useReducedMotion();

  const statistics = [
    { number: 831, suffix: '+', label: 'Research Papers', icon: FileText },
    { number: 1100, suffix: '+', label: 'Thesis Repositories', icon: Award },
    { number: 50, suffix: '+', label: 'AI Powered Textbooks', icon: GraduationCap },
    { number: 50, suffix: '+', label: 'Biographical Articles', icon: Users },
  ];

  const upcomingFeatures = [
    { icon: Brain, title: 'AI-Powered Learning', description: 'Intelligent diagnosis assistant and personalized learning paths', phase: 'Phase 2', status: 'In Development' },
    { icon: Globe, title: 'VR/AR Simulations', description: 'Immersive virtual surgical training environment', phase: 'Phase 2', status: 'Planning' },
    { icon: Activity, title: 'Real-time OPD Integration', description: 'Live case studies and clinical integration', phase: 'Phase 3', status: 'Planned' },
    { icon: Users, title: 'Global Collaboration', description: 'International network of Ayurvedic practitioners', phase: 'Phase 3', status: 'Planned' },
    { icon: Globe, title: 'International Surgery Directory', description: 'Comprehensive global directory of Ayurvedic surgeons', phase: 'Phase 3', status: 'Planning' },
    { icon: Users, title: 'Mentor-Mentee Network', description: 'Connect experienced practitioners with aspiring surgeons', phase: 'Phase 3', status: 'Planning' },
    { icon: FileText, title: 'Journal of Surgical Research', description: 'Peer-reviewed publication platform for Shalya Tantra', phase: 'Phase 3', status: 'Planning' },
    { icon: Award, title: 'Shalya Chikitsak Association', description: 'Global professional body for surgical practitioners', phase: 'Phase 3', status: 'Planning' },
  ];

  return (
    <div className="home-page about-page">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content text-center fade-in-up">
            <h1>
              <span className="sanskrit">वैदिक</span> Surgery
            </h1>
            <div className="hero-subtitle-container">
              <div className="hero-subtitle-lines-1-2">
                <p>
                  Revolutionizing Ayurvedic surgical education<br />
                  by integrating classical Shalya Tantra wisdom with modern surgical science
                </p>
              </div>
              <div className="hero-subtitle-line3">
                <Typewriter
                  options={{
                    strings: ['<strong>AI-Powered Learning</strong>'],
                    autoStart: true,
                    delay: 100,
                    deleteSpeed: Infinity,
                    cursor: '|',
                    loop: false,
                    startDelay: 1200,
                  }}
                />
              </div>
            </div>
            <div className="hero-buttons">
              <Link to="/acharya-sushruta" className="btn btn-primary btn-large">
                <Users size={20} />
                Know Acharya Sushruta
              </Link>
              <Link to="/shalya-tantra" className="btn btn-secondary btn-large">
                <BookOpen size={20} />
                Explore Shalya Tantra
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission Section */}
      <section className="section">
        <div className="container">
          <div className="grid grid-2">
            <Reveal className="vision-mission-card card" delay={0}>
              <div className="card-icon">
                <Globe size={24} />
              </div>
              <h3>Our Vision</h3>
              <p>
                To revolutionize Ayurvedic surgical education by integrating classical Shalya Tantra wisdom
                with modern surgical science and AI-powered learning, creating a globally recognized and
                clinically effective platform for future Vaidyas.
              </p>
            </Reveal>
            <Reveal className="mission-card card" delay={0.12}>
              <div className="card-icon">
                <Heart size={24} />
              </div>
              <h3>Our Mission</h3>
              <ul>
                <li>Preserve and digitize classical Shalya Tantra teachings</li>
                <li>Develop AI-assisted educational ecosystems</li>
                <li>Bridge Ayurveda with contemporary surgery</li>
                <li>Establish India's first AI-integrated surgical platform</li>
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      {/* AYUSH Care Spotlight */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="home-spotlight card">
              <div className="home-spotlight-glow" aria-hidden="true" />
              <div className="home-spotlight-body">
                <span className="home-spotlight-badge">
                  <Sparkles size={14} /> SaaS Engine
                </span>
                <h2>Personalized AYUSH Care</h2>
                <p>
                  Our AI Clinical Engine turns a patient's Prakriti and condition into a complete,
                  evidence-grounded care plan — herbs, yoga, and diet — with full Ayurvedic rationale,
                  outcome tracking, and AI-assisted adjustments.
                </p>
                <div className="home-spotlight-pills">
                  <span className="home-spotlight-pill"><Leaf size={14} /> Prakriti Assessment</span>
                  <span className="home-spotlight-pill"><Brain size={14} /> AI Clinical Engine</span>
                  <span className="home-spotlight-pill"><Activity size={14} /> Treatment Plans</span>
                </div>
                <Link to="/treatment" className="btn btn-primary btn-large">
                  <Stethoscope size={20} />
                  Explore AYUSH Care Portal
                </Link>
              </div>
              <div className="home-spotlight-visual" aria-hidden="true">
                <div className="home-spotlight-orb">
                  <Stethoscope size={64} />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="section section-alt">
        <div className="container">
          <Reveal>
            <div className="content-header">
              <h2>Platform Impact</h2>
              <p>Transforming Ayurvedic surgical education across India and beyond</p>
            </div>
          </Reveal>
          <div className="stats-grid">
            {statistics.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-item card"
                initial={reduce ? false : { opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: index * 0.08 }}
              >
                <div className="stat-icon">
                  <stat.icon size={32} color="var(--primary-color)" />
                </div>
                <CountUp value={stat.number} suffix={stat.suffix} className="stat-number" />
                <span className="stat-label">{stat.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder Message Section */}
      <motion.section
        className="section founder-section"
        initial={reduce ? false : { opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: '-60px' }}
        transition={{ duration: 0.7 }}
      >
        <div className="container">
          <div className="content-header text-center">
            <h2>Meet the Founder</h2>
            <p>Pioneering the integration of ancient Ayurvedic wisdom with modern surgical science</p>
          </div>
          <div className="founder-container">
            <div className="founder-image-wrapper">
              <div className="founder-image-frame">
                <img src={founderPhoto} alt="Dr. Sameep Singh - Founder" className="founder-image" />
              </div>
            </div>
            <div className="founder-content">
              <div className="founder-header">
                <h3>Dr. Sameep Singh</h3>
                <p className="founder-credentials">
                  BAMS, PG Scholar (MS Shalya Tantra)<br />
                  Quadra Institute of Ayurveda & Hospital, Roorkee
                </p>
              </div>
              <blockquote className="founder-quote">
                <div className="quote-mark">"</div>
                <p>
                  The future of Ayurvedic surgery lies in preserving our ancient wisdom while embracing
                  modern technology. Through this platform, we aim to create a bridge between the timeless
                  teachings of Maharishi Sushruta and contemporary surgical practice, ensuring that future
                  generations of Vaidyas are equipped with both traditional knowledge and modern tools.
                </p>
              </blockquote>
              <div className="founder-social">
                <a href="mailto:vedicsurgery@gmail.com" className="social-link">
                  <Mail size={20} />
                  <span>Get in Touch</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Development Roadmap */}
      <section className="section section-alt roadmap-section">
        <div className="container">
          <Reveal>
            <div className="content-header text-center">
              <div className="section-badge">
                <Zap size={20} />
                <span>Product Roadmap</span>
              </div>
              <h2>Future Innovations</h2>
              <p>Pioneering the next generation of Ayurvedic surgical education</p>
            </div>
          </Reveal>

          <div className="roadmap-scroll-container">
            <div className="roadmap-scroll-track">
              {upcomingFeatures.concat(upcomingFeatures).map((feature, index) => (
                <div key={index} className="roadmap-card card">
                  <div className="roadmap-card-header">
                    <div className="roadmap-icon">
                      <feature.icon size={28} />
                    </div>
                    <div className="roadmap-status">
                      <Clock size={14} />
                      <span>{feature.status}</span>
                    </div>
                  </div>
                  <div className="roadmap-card-content">
                    <div className="roadmap-phase">{feature.phase}</div>
                    <h3>{feature.title}</h3>
                    <p>{feature.description}</p>
                  </div>
                  <div className="roadmap-card-footer">
                    <RoadmapProgress status={feature.status.toLowerCase().replace(' ', '-')} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <Reveal>
            <div className="roadmap-timeline">
              <div className="timeline-header">
                <h3>Development Roadmap</h3>
                <p>Our journey towards revolutionizing Ayurvedic surgical education</p>
              </div>
              <div className="timeline-track">
                <div className="timeline-milestone completed">
                  <div className="milestone-marker">
                    <Award size={20} />
                  </div>
                  <div className="milestone-content">
                    <span className="milestone-date">Q4 2025</span>
                    <h4>Phase 1 Complete</h4>
                    <p>Platform launch with core features and content</p>
                  </div>
                </div>
                <div className="timeline-milestone active">
                  <div className="milestone-marker">
                    <Brain size={20} />
                  </div>
                  <div className="milestone-content">
                    <span className="milestone-date">Q1-Q2 2026</span>
                    <h4>Phase 2 Development</h4>
                    <p>AI integration and VR/AR simulations</p>
                  </div>
                </div>
                <div className="timeline-milestone upcoming">
                  <div className="milestone-marker">
                    <Globe size={20} />
                  </div>
                  <div className="milestone-content">
                    <span className="milestone-date">Q3-Q4 2026</span>
                    <h4>Phase 3 Expansion</h4>
                    <p>Global collaboration and advanced features</p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
